import React, { useState, useMemo } from 'react';
import { 
  ArrowLeft, 
  Briefcase, 
  Plus, 
  Trash2, 
  Clock, 
  Search,
  CheckCircle2,
  AlertCircle,
  Wrench,
  DollarSign,
  ChevronRight,
  Send,
  Loader2,
  Zap,
  X,
  PlayCircle,
  Tag,
  Flame,
  Share2,
  Percent,
  Users as UsersIcon,
  Calendar as CalendarIcon,
  Copy,
  Check
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { TUTORIAL_LINKS } from '../constants';
import { UserProfile, FinancialConfig, SimpleService, ServicePromotion, PromoLimitType } from '../types';
import { getLocalDateString } from '../modules/erp/utils/calendarUtils';
import { formatCurrencyBRL, formatCurrencyMask, parseCurrencyMask } from '../utils/formatUtils';
import { generateContentWithCache } from '../lib/geminiService';
import { parseAIJSON } from '../utils/aiUtils';
import { useNicheServiceTemplates } from '../hooks/useNicheServiceTemplates';
import { consumeServiceDrafts, BRIDGE_EVENTS } from '../modules/erp/utils/commandBridge';
import {
  calculateDiscountPercent,
  calculateDiscountAmount,
  isPromoActive,
  getPromoUrgencyText,
  isSchedulingNiche,
  generatePromoWhatsAppText
} from '../utils/promoUtils';

const CurrencyInput = ({ value, onChange, className, placeholder }: { value: number, onChange: (val: number) => void, className?: string, placeholder?: string }) => {
  const [displayValue, setDisplayValue] = React.useState(() => {
    return (value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  });

  React.useEffect(() => {
    const parentVal = (value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (parentVal !== displayValue && Math.abs((parseCurrencyMask(displayValue) || 0) - value) > 0.001) {
      setDisplayValue(parentVal);
    }
  }, [value, displayValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let digits = e.target.value.replace(/\D/g, '');
    if (!digits) digits = '0';
    const numericValue = parseInt(digits, 10) / 100;
    
    setDisplayValue(numericValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    onChange(numericValue);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleChange}
      className={className}
      placeholder={placeholder}
    />
  );
};

interface Props {
  user: UserProfile;
  config: FinancialConfig;
  services: SimpleService[];
  onSaveService: (service: SimpleService) => void;
  onDeleteService: (id: string) => void;
  onBack: () => void;
}

export default function ServiceManager({ user, config, services, onSaveService, onDeleteService, onBack }: Props) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<Partial<SimpleService> | null>(null);
  const [copiedDirectLink, setCopiedDirectLink] = useState(false);

  const bookingSlug = (user.businessName || user.ownerName || 'agendamento')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [pendingServices, setPendingServices] = useState<Partial<SimpleService>[]>([]);
  const isShrunk = false;

  // Ponte Omnibar -> Catálogo de Serviços: recebe os serviços lidos pelo comando na fila de validação
  React.useEffect(() => {
    const receiveStaged = () => {
      const staged = consumeServiceDrafts(user.id);
      if (staged.length === 0) return;
      setPendingServices(prev => [
        ...prev,
        ...staged.map(s => ({
          id: crypto.randomUUID(),
          userId: user.id,
          name: s.name,
          type: s.type,
          value: s.value,
          description: s.description,
          createdAt: new Date().toISOString()
        }))
      ]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    receiveStaged();
    window.addEventListener(BRIDGE_EVENTS.services, receiveStaged);
    return () => window.removeEventListener(BRIDGE_EVENTS.services, receiveStaged);
  }, [user.id]);

  const filteredServices = useMemo(() => {
    return services.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [services, searchTerm]);

  const { suggestions: serviceSuggestions, dismissSuggestion: dismissServiceSuggestion } = useNicheServiceTemplates({
    userNiche: user.niche || '',
    existingServices: services,
    userId: user.id,
  });

  const handleQuickAddService = (s: { id: string; name: string; type: 'fixed' | 'hourly'; description?: string }) => {
    setEditingService({
      name: s.name,
      type: s.type,
      value: 0,
      description: s.description || '',
    });
    setIsModalOpen(true);
  };

  const processWithAI = async (text: string) => {
    if (!text) return;
    setIsProcessing(true);
    setProcessingStep('Iniciando análise...');
    
    try {
      setProcessingStep('Identificando modelos de cobrança...');
      const prompt = `Você é um extrator flexível do sistema R-MAX.
Extraia todos os serviços listados no texto.
Retorne rigorosamente APENAS um JSON no esquema abaixo (sem markdown, SOMENTE JSON PURO):
{
  "services": [
    {
      "name": "Nome do serviço",
      "type": "hourly" ou "fixed",
      "value": 150.00
    }
  ]
}
Regras:
1. "type": Se for serviço cobrado por tempo/hora, use "hourly". Se for valor fechado/pacote/diária, use "fixed".
2. "value": Obrigatório ser um NÚMERO decimal simples (ex: 15.90, NUNCA string). Se não encontrar valor, coloque 0.`;

      const response = await generateContentWithCache({
        model: 'gemini-3.5-flash-lite',
        contents: [{ role: 'user', parts: [{ text: `${prompt}\n\Mensagem do Usuário:\n"${text}"` }] }],
        config: {
          generationConfig: {
            responseMimeType: 'application/json'
          }
        }
      });
      
      setProcessingStep('Ajustando valores e prazos...');
      const parsed = parseAIJSON<{ services: any[] }>(response.text, { services: [] });
      let results: Partial<SimpleService>[] = [];
      
      if (parsed && Array.isArray(parsed.services)) {
         results = parsed.services.map(item => ({
            id: crypto.randomUUID(),
            userId: user.id,
            name: item.name || 'Serviço Desconhecido',
            type: item.type === 'hourly' ? 'hourly' : 'fixed',
            value: typeof item.value === 'number' ? item.value : 0,
            createdAt: new Date().toISOString()
         }));
      }

      if (results.length > 0) {
        setPendingServices(prev => [...prev, ...results]);
        setInputValue('');
        setProcessingStep('Pronto!');
        // Scroll para o topo para garantir visibilidade da fila de validação
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        alert("O R-Max não conseguiu identificar serviços. Tente reescrever o texto.");
      }
    } catch (err) {
      console.error('AI processing error:', err);
      alert("Houve uma falha ao contatar o R-Max. Tente novamente.");
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
    }
  };

  const confirmPendingService = (service: Partial<SimpleService>) => {
    if (!service.name) {
       alert("Dê um nome ao serviço!");
       return;
    }
    onSaveService({
      ...service,
      id: service.id || crypto.randomUUID(),
      userId: user.id,
      createdAt: service.createdAt || new Date().toISOString()
    } as SimpleService);
    setPendingServices(prev => prev.filter(p => p.id !== service.id));
  };

  const updatePendingService = (id: string, field: keyof SimpleService, value: any) => {
    setPendingServices(prev => prev.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleCreateNew = () => {
    setEditingService({
      userId: user.id,
      name: '',
      value: 0,
      type: 'fixed',
      createdAt: new Date().toISOString()
    });
    setIsModalOpen(true);
  };

  const handleEdit = (service: SimpleService) => {
    setEditingService({ ...service });
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (!editingService?.name) {
      alert('Dê um nome ao serviço!');
      return;
    }
    onSaveService({
      ...editingService,
      id: editingService.id || crypto.randomUUID()
    } as SimpleService);
    setIsModalOpen(false);
  };

  return (
    <div className="pb-32 font-sans relative">
      <div className="px-4 pt-6 space-y-6">
        {/* Banner de Link de Agendamento Online com Horário de Funcionamento */}
        <div className="bg-emerald-50 border border-emerald-200/90 rounded-3xl p-4 sm:p-5 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] font-black uppercase tracking-wider text-emerald-800">
                Seu Link de Agendamento Online
              </span>
            </div>
            <p className="text-xs font-black text-zinc-900 break-all">
              {typeof window !== 'undefined' ? window.location.origin : 'https://precificacao.resolvmax.com.br'}/agendar/{bookingSlug}
            </p>
            <div className="flex items-center gap-1.5 text-[11px] text-zinc-600 font-medium pt-0.5">
              <Clock className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
              <span>Horário de Atendimento: <strong>Terça a Sábado: 08:00 às 19:00</strong></span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => {
                const link = `${window.location.origin}/agendar/${bookingSlug}`;
                navigator.clipboard.writeText(link);
                setCopiedDirectLink(true);
                setTimeout(() => setCopiedDirectLink(false), 3000);
              }}
              className="px-3.5 py-2 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer active:scale-95"
            >
              {copiedDirectLink ? <Check className="h-4 w-4 stroke-3" /> : <Copy className="h-4 w-4" />}
              <span>{copiedDirectLink ? 'Copiado!' : 'Copiar Link'}</span>
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`Olá! Faça seu agendamento online com antecedência pelo nosso link: ${window.location.origin}/agendar/${bookingSlug}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded-xl transition-colors flex items-center justify-center cursor-pointer"
              title="Compartilhar no WhatsApp"
            >
              <Share2 className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* AI Extraction Card */}
        <div className="relative z-10">
          <div className={`bg-white border border-zinc-100 shadow-xl border-b-4 border-b-indigo-200 transition-all duration-300 ${
            isShrunk ? 'rounded-none p-3' : 'rounded-4xl p-6'
          }`}>
            <AnimatePresence mode="wait">
              {!isShrunk ? (
                <motion.div
                  key="full-input"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-4"
                >
                  <div className="flex items-center gap-2">
                    <div className="bg-indigo-500/10 p-2 rounded-2xl">
                      <Zap className="h-5 w-5 text-indigo-600" />
                    </div>
                    <h3 className="font-black text-zinc-900 uppercase text-[10px] tracking-widest">Extração Rápida de Serviços</h3>
                  </div>
                  
                  <textarea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Ex: Hora de design 150, Faxina fechada 200..."
                    className="w-full bg-zinc-50 border-none rounded-2xl p-5 text-sm focus:ring-2 focus:ring-indigo-500/20 min-h-25 resize-none font-medium"
                  />

                  <button
                    onClick={() => inputValue && processWithAI(inputValue)}
                    disabled={isProcessing || !inputValue}
                    className="w-full bg-indigo-600 text-white py-4 rounded-3xl font-black text-xs uppercase tracking-[0.2em] hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center justify-center gap-3 active:scale-95 shadow-lg shadow-indigo-600/20"
                  >
                    {isProcessing ? (
                      <div className="flex items-center gap-3">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>{processingStep}</span>
                      </div>
                    ) : (
                      <>
                        <Send className="h-5 w-5 text-indigo-300" />
                        <span>Processar Serviços</span>
                      </>
                    )}
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="shrunk-input"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="flex items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-2 overflow-hidden">
                    <Zap className="h-5 w-5 text-indigo-500 shrink-0" />
                    <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest truncate">
                      {isProcessing ? processingStep : 'Toque para extrair novos serviços'}
                    </p>
                  </div>
                  <button 
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="bg-indigo-600 text-white p-2.5 rounded-xl shadow-lg shadow-indigo-600/20 active:scale-95 transition-all"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Validation Queue (Pending Services) */}
        {pendingServices.length > 0 && (
          <div className="bg-white border-2 border-indigo-500/20 rounded-3xl p-5 shadow-xl animate-in fade-in zoom-in-95 duration-300">
            <div className="flex items-center justify-between mb-5 border-b border-zinc-100 pb-4">
              <h4 className="text-xs font-black uppercase tracking-[0.15em] text-zinc-700 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-indigo-600" /> 
                Validando Novos Serviços
              </h4>
              <span className="bg-indigo-50 text-indigo-700 font-bold text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border border-indigo-200">
                {pendingServices.length} {pendingServices.length === 1 ? 'pendente' : 'pendentes'}
              </span>
            </div>
            
            {(() => {
              const service = pendingServices[0];
              return (
                <div key={service.id} className="animate-in slide-in-from-right-4 duration-300">
                  <div className="flex flex-col gap-5 mb-6">
                    <p className="text-xs text-zinc-600 px-1">
                      Confira se o nome, tipo de cobrança e valor estão corretos antes de salvar no catálogo.
                    </p>
                    
                    <div>
                      <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide mb-1.5 block px-1">Nome do Serviço</label>
                      <input
                        type="text"
                        value={service.name || ''}
                        onChange={(e) => updatePendingService(service.id!, 'name', e.target.value)}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-3.5 px-4 text-sm font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide mb-1.5 block px-1">Tipo de Cobrança</label>
                        <select
                          value={service.type}
                          onChange={(e) => updatePendingService(service.id!, 'type', e.target.value as any)}
                          className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-3.5 px-4 text-sm font-bold text-zinc-900 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                        >
                          <option value="hourly">Por Hora</option>
                          <option value="fixed">Valor Fixo</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide mb-1.5 block px-1">Valor Base</label>
                        <CurrencyInput
                          value={service.value || 0}
                          onChange={(val) => updatePendingService(service.id!, 'value', val)}
                          className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-3.5 px-4 text-sm font-mono font-black text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => confirmPendingService(service)}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3.5 rounded-2xl font-bold text-xs uppercase tracking-wider shadow-md shadow-indigo-600/20 active:scale-95 transition-all cursor-pointer"
                    >
                      {pendingServices.length > 1 ? 'Confirmar e Próximo' : 'Tudo Certo! Confirmar'}
                    </button>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setPendingServices(prev => prev.slice(1))}
                        className="flex-1 bg-zinc-100 text-zinc-700 py-3 rounded-2xl hover:text-indigo-700 hover:bg-indigo-50 border border-zinc-200 transition-colors font-bold uppercase text-xs tracking-wider cursor-pointer"
                      >
                        Pular
                      </button>
                      <button
                        onClick={() => {
                          if (confirm("Deseja descartar este serviço?")) {
                            setPendingServices(prev => prev.filter(p => p.id !== service.id));
                          }
                        }}
                        className="flex-1 bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 py-3 rounded-2xl transition-colors font-bold uppercase text-xs tracking-wider cursor-pointer"
                      >
                        Descartar
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Services List Section */}
        <div className="space-y-6">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Meus Serviços Cadastrados ({services.length})</h3>
            <div className="flex items-center gap-3">
              <a 
                href={TUTORIAL_LINKS.servicos}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 bg-indigo-50 border border-indigo-200/60 px-2.5 py-1 rounded-xl text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 hover:bg-indigo-100 transition-colors"
              >
                <PlayCircle className="h-3.5 w-3.5 text-indigo-600" />
                Vídeo Tutorial
              </a>
              <button 
                onClick={handleCreateNew}
                className="text-indigo-600 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 hover:underline"
              >
                <Plus className="h-3 w-3" />
                Novo Serviço
              </button>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <input 
              type="text"
              placeholder="Buscar serviço..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white border border-zinc-200 rounded-2xl py-4 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 shadow-sm transition-all"
            />
          </div>

          {/* Suggestions Panel */}
          {serviceSuggestions.length > 0 && (
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 px-1">
                <Zap className="h-3 w-3 text-indigo-500" />
                <h3 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Sugeridos para {user.niche}</h3>
              </div>
              <div className="flex flex-col gap-2">
                {serviceSuggestions.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between bg-white border border-zinc-100 rounded-2xl px-4 py-3 shadow-sm"
                  >
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold text-zinc-700 truncate">{s.name}</p>
                      <p className="text-[9px] text-zinc-400 uppercase tracking-wide mt-0.5">
                        {s.type === 'hourly' ? 'Por Hora' : 'Valor Fixo'}
                        {s.description && ` · ${s.description}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-3 shrink-0">
                      <button
                        onClick={() => handleQuickAddService(s)}
                        className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 text-[10px] font-black px-3 py-1.5 rounded-xl transition-colors flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" />
                        Adicionar
                      </button>
                      <button
                        onClick={() => dismissServiceSuggestion(s.id)}
                        title="Não preciso deste serviço"
                        className="text-zinc-300 hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-50"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredServices.length === 0 ? (
              <div className="col-span-full py-16 text-center bg-white rounded-4xl border-2 border-dashed border-zinc-100">
                <Briefcase className="h-16 w-16 text-zinc-200 mx-auto mb-4" />
                <h4 className="text-zinc-900 font-bold mb-1">Nenhum serviço encontrado</h4>
                <p className="text-zinc-400 text-xs max-w-60 mx-auto leading-relaxed">
                  {searchTerm ? 'Tente buscar por outro nome ou extraia um novo serviço acima.' : 'Use o quadro de extração no topo para começar agora!'}
                </p>
              </div>
            ) : (
              filteredServices.map(service => {
                const hasPromo = isPromoActive(service.promo);
                const discountPercent = hasPromo ? calculateDiscountPercent(service.value, service.promo!.promoPrice) : 0;
                const urgencyText = hasPromo ? getPromoUrgencyText(service.promo) : null;

                return (
                  <div
                    key={service.id}
                    onClick={() => handleEdit(service)}
                    className={`bg-white p-6 rounded-4xl border transition-all text-left flex flex-col group relative overflow-hidden cursor-pointer ${
                      hasPromo
                        ? 'border-amber-300 shadow-md shadow-amber-500/5 hover:border-amber-500 ring-1 ring-amber-400/30'
                        : 'border-zinc-100 shadow-sm hover:border-indigo-500'
                    }`}
                  >
                    <div className="absolute top-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ChevronRight className={`h-5 w-5 ${hasPromo ? 'text-amber-500' : 'text-indigo-500'}`} />
                    </div>

                    <div className="flex justify-between items-start mb-4">
                      <div className={`p-3 rounded-2xl ${
                        hasPromo
                          ? 'bg-amber-50 text-amber-600'
                          : service.type === 'hourly'
                          ? 'bg-indigo-50 text-indigo-600'
                          : 'bg-emerald-50 text-emerald-600'
                      }`}>
                        {hasPromo ? (
                          <Flame className="h-5 w-5 text-amber-500" />
                        ) : service.type === 'hourly' ? (
                          <Clock className="h-5 w-5" />
                        ) : (
                          <DollarSign className="h-5 w-5" />
                        )}
                      </div>

                      {hasPromo && (
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-[10px] font-black bg-linear-to-r from-amber-500 to-orange-500 text-white px-2.5 py-1 rounded-full shadow-xs uppercase tracking-wider flex items-center gap-1">
                            <Flame className="h-3 w-3" />
                            {service.promo?.title ? `${service.promo.title.slice(0, 18)} • ` : ''}{discountPercent}% OFF
                          </span>
                          {urgencyText && (
                            <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-100">
                              {urgencyText}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <h3 className="text-base font-bold text-zinc-900 mb-1 truncate pr-6">{service.name}</h3>
                    <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-4">
                      {service.type === 'hourly' ? 'Por Hora' : 'Valor Fixo'}
                    </p>
                    
                    <div className="mt-auto pt-4 border-t border-zinc-50 flex items-end justify-between gap-2">
                      <div>
                        {hasPromo ? (
                          <div>
                            <span className="text-xs text-zinc-400 font-bold line-through block">
                              De {formatCurrencyBRL(service.value)}
                            </span>
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-[11px] font-bold text-amber-600">Por</span>
                              <p className="text-2xl font-black text-amber-600">
                                {formatCurrencyBRL(service.promo!.promoPrice)}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-2xl font-black text-zinc-900">{formatCurrencyBRL(service.value)}</p>
                        )}
                      </div>

                      {hasPromo && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const shareText = generatePromoWhatsAppText({
                              businessName: user.businessName || user.ownerName || user.fullName || 'Minha Empresa',
                              serviceName: service.name,
                              originalPrice: service.value,
                              promoPrice: service.promo!.promoPrice,
                              promoTitle: service.promo?.title,
                              urgencyNote: urgencyText,
                              bookingUrl: window.location.origin
                            });
                            window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`, '_blank');
                          }}
                          className="p-2 bg-emerald-50 hover:bg-emerald-100 text-brand rounded-xl font-bold text-[11px] flex items-center gap-1.5 border border-emerald-200/60 transition-colors shadow-xs"
                          title="Divulgar Oferta no WhatsApp"
                        >
                          <Share2 className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Divulgar</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Service Modal */}
      <AnimatePresence>
        {isModalOpen && editingService && (
          <div className="fixed inset-0 z-100 flex items-center justify-center p-2.5 sm:p-4 overflow-y-auto">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-zinc-900/80 backdrop-blur-sm" 
            />
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              className="relative w-full max-w-lg bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[min(90vh,calc(100dvh-2rem))] my-auto"
            >
              <div className="p-6 border-b border-zinc-100 flex justify-between items-center bg-zinc-50/50 shrink-0">
                <div>
                  <h2 className="text-xl font-bold text-zinc-900">
                    {editingService.id ? 'Editar Serviço' : 'Novo Serviço'}
                  </h2>
                  <p className="text-xs text-zinc-500">Defina o nome e o modelo de cobrança.</p>
                </div>
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="bg-zinc-200/50 hover:bg-zinc-200 p-2 rounded-full transition-colors cursor-pointer"
                >
                  <ArrowLeft className="h-5 w-5 text-zinc-600 rotate-180" />
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-6 space-y-6">
                <div>
                  <label className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.15em] block mb-2">Nome do Serviço</label>
                  <input 
                    type="text" 
                    placeholder="Ex: Visita Técnica, Diária, Hora Extra"
                    value={editingService.name}
                    onChange={e => setEditingService({...editingService, name: e.target.value})}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-3.5 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 font-bold"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setEditingService({...editingService, type: 'fixed'})}
                    className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                      editingService.type === 'fixed' 
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700 shadow-md shadow-indigo-100' 
                        : 'border-zinc-100 bg-zinc-50 text-zinc-400 grayscale'
                    }`}
                  >
                    <DollarSign className="h-6 w-6" />
                    <span className="text-xs font-bold uppercase tracking-widest">Valor Fixo</span>
                  </button>
                  <button
                    onClick={() => setEditingService({...editingService, type: 'hourly'})}
                    className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                      editingService.type === 'hourly' 
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700 shadow-md shadow-indigo-100' 
                        : 'border-zinc-100 bg-zinc-50 text-zinc-400 grayscale'
                    }`}
                  >
                    <Clock className="h-6 w-6" />
                    <span className="text-xs font-bold uppercase tracking-widest">Por Hora</span>
                  </button>
                </div>

                <div>
                  <label className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.15em] block mb-2">
                    {editingService.type === 'fixed' ? 'Valor do Serviço (R$)' : 'Valor da Hora (R$)'}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-zinc-400">R$</span>
                    <CurrencyInput
                      value={editingService.value || 0}
                      onChange={val => setEditingService({...editingService, value: val})}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-3.5 pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 font-mono font-black text-indigo-600"
                    />
                  </div>
                </div>

                {editingService.type === 'hourly' && (
                  <div className="bg-amber-50 rounded-2xl p-4 flex gap-3 items-start border border-amber-100">
                    <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-[10px] text-amber-800 leading-relaxed font-medium italic">
                      Dica: O seu custo base de pro-labore hoje é {formatCurrencyBRL(config.baseHourValue)}/h. Lembre de cobrar acima disso para ter lucro real.
                    </p>
                  </div>
                )}

                {/* Seção de Oferta Promocional (De / Por com cálculo automático de % OFF) */}
                <div className="bg-linear-to-br from-amber-500/5 via-amber-500/10 to-orange-500/5 rounded-3xl p-5 border border-amber-200/70 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 bg-amber-500 text-white rounded-xl shadow-xs">
                        <Flame className="h-4 w-4" />
                      </div>
                      <div>
                        <h4 className="text-xs font-black text-zinc-900 uppercase tracking-wider flex items-center gap-1.5">
                          <span>Oferta / Promoção</span>
                          <span className="text-[9px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-black">
                            DE / POR
                          </span>
                        </h4>
                        <p className="text-[11px] text-zinc-500 font-medium">
                          Ative um valor especial com cálculo automático de desconto.
                        </p>
                      </div>
                    </div>

                    {/* Switch Toggle */}
                    <button
                      type="button"
                      onClick={() => {
                        const currentPromo = editingService.promo;
                        const isActivating = !currentPromo?.active;
                        setEditingService({
                          ...editingService,
                          promo: {
                            active: isActivating,
                            promoPrice: currentPromo?.promoPrice || (editingService.value ? Math.round(editingService.value * 0.8) : 0),
                            title: currentPromo?.title || 'Promoção da Semana',
                            limitType: currentPromo?.limitType || 'none',
                            maxUsage: currentPromo?.maxUsage || 10,
                            currentUsage: currentPromo?.currentUsage || 0,
                            startDate: currentPromo?.startDate || getLocalDateString(),
                            endDate: currentPromo?.endDate || ''
                          }
                        });
                      }}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        editingService.promo?.active ? 'bg-amber-500' : 'bg-zinc-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                          editingService.promo?.active ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {editingService.promo?.active && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="space-y-4 pt-2 border-t border-amber-200/50"
                    >
                      {/* Input do Valor Promocional */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-[10px] font-black text-amber-900 uppercase tracking-widest block">
                            Valor Promocional da Oferta (R$)
                          </label>
                          {editingService.value > 0 && editingService.promo.promoPrice > 0 && editingService.promo.promoPrice < editingService.value && (
                            <span className="text-[10px] font-black bg-emerald-500 text-white px-2 py-0.5 rounded-full shadow-xs flex items-center gap-1">
                              <Flame className="h-3 w-3" />
                              {calculateDiscountPercent(editingService.value, editingService.promo.promoPrice)}% OFF
                            </span>
                          )}
                        </div>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-amber-700">R$</span>
                          <CurrencyInput
                            value={editingService.promo.promoPrice || 0}
                            onChange={val => setEditingService({
                              ...editingService,
                              promo: {
                                ...editingService.promo!,
                                promoPrice: val
                              }
                            })}
                            className="w-full bg-white border border-amber-300 rounded-2xl py-3 pl-11 pr-4 text-sm font-mono font-black text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                            placeholder="0,00"
                          />
                        </div>
                      </div>

                      {/* Feedback visual dinâmico de De / Por */}
                      {editingService.value > 0 && editingService.promo.promoPrice > 0 && (
                        editingService.promo.promoPrice < editingService.value ? (
                          <div className="bg-emerald-50 border border-emerald-200/80 rounded-2xl p-3 flex items-center justify-between text-xs">
                            <div>
                              <span className="text-[10px] text-zinc-400 font-bold block uppercase tracking-wider">Visual do Cliente:</span>
                              <div className="flex items-center gap-2">
                                <span className="text-zinc-400 line-through font-bold text-[11px]">
                                  De {formatCurrencyBRL(editingService.value)}
                                </span>
                                <span className="font-black text-brand text-sm">
                                  Por {formatCurrencyBRL(editingService.promo.promoPrice)}
                                </span>
                              </div>
                            </div>
                            <span className="text-[11px] font-bold text-brand bg-white px-2.5 py-1 rounded-xl border border-emerald-100 shadow-xs">
                              Economia de {formatCurrencyBRL(calculateDiscountAmount(editingService.value, editingService.promo.promoPrice))}
                            </span>
                          </div>
                        ) : (
                          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-3 text-[11px] text-rose-700 font-medium">
                            ⚠️ O valor promocional deve ser menor que o valor original ({formatCurrencyBRL(editingService.value)}).
                          </div>
                        )
                      )}

                      {/* Nome da Ação Promocional */}
                      <div>
                        <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest block mb-1.5">
                          Nome da Promoção (Opcional)
                        </label>
                        <input
                          type="text"
                          placeholder="Ex: Semana do Cabelo Colorido, Quarta da Barba, Oferta VIP"
                          value={editingService.promo.title || ''}
                          onChange={e => setEditingService({
                            ...editingService,
                            promo: {
                              ...editingService.promo!,
                              title: e.target.value
                            }
                          })}
                          className="w-full bg-white border border-zinc-200 rounded-2xl py-2.5 px-3.5 text-xs font-bold text-zinc-800 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                        />
                      </div>

                      {/* Gatilho de Escassez / Validade */}
                      <div>
                        <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest block mb-2">
                          Regra de Validade da Oferta
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          <button
                            type="button"
                            onClick={() => setEditingService({
                              ...editingService,
                              promo: { ...editingService.promo!, limitType: 'none' }
                            })}
                            className={`p-2.5 rounded-xl border text-[11px] font-bold transition-all ${
                              editingService.promo.limitType === 'none' || !editingService.promo.limitType
                                ? 'bg-amber-500 text-white border-amber-500 shadow-xs'
                                : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'
                            }`}
                          >
                            Sem Limite
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingService({
                              ...editingService,
                              promo: { ...editingService.promo!, limitType: 'quantity', maxUsage: editingService.promo?.maxUsage || 10 }
                            })}
                            className={`p-2.5 rounded-xl border text-[11px] font-bold transition-all ${
                              editingService.promo.limitType === 'quantity'
                                ? 'bg-amber-500 text-white border-amber-500 shadow-xs'
                                : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'
                            }`}
                          >
                            Por Vagas
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingService({
                              ...editingService,
                              promo: { ...editingService.promo!, limitType: 'date' }
                            })}
                            className={`p-2.5 rounded-xl border text-[11px] font-bold transition-all ${
                              editingService.promo.limitType === 'date'
                                ? 'bg-amber-500 text-white border-amber-500 shadow-xs'
                                : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'
                            }`}
                          >
                            Por Data
                          </button>
                        </div>
                      </div>

                      {/* Campos específicos do tipo de limite */}
                      {editingService.promo.limitType === 'quantity' && (
                        <div className="bg-white p-3.5 rounded-2xl border border-amber-200 flex items-center justify-between gap-3">
                          <div>
                            <span className="text-xs font-bold text-zinc-900 block">Quantidade de Vagas</span>
                            <span className="text-[10px] text-zinc-400">Oferta válida para os primeiros X clientes</span>
                          </div>
                          <div className="w-24">
                            <input
                              type="number"
                              min="1"
                              max="999"
                              value={editingService.promo.maxUsage || 10}
                              onChange={e => setEditingService({
                                ...editingService,
                                promo: { ...editingService.promo!, maxUsage: parseInt(e.target.value, 10) || 1 }
                              })}
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-2 px-3 text-center text-sm font-black text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                            />
                          </div>
                        </div>
                      )}

                      {editingService.promo.limitType === 'date' && (
                        <div className="bg-white p-3.5 rounded-2xl border border-amber-200 flex items-center justify-between gap-3">
                          <div>
                            <span className="text-xs font-bold text-zinc-900 block">Data de Término</span>
                            <span className="text-[10px] text-zinc-400">Oferta expira automaticamente após esta data</span>
                          </div>
                          <div className="w-40">
                            <input
                              type="date"
                              value={editingService.promo.endDate || ''}
                              onChange={e => setEditingService({
                                ...editingService,
                                promo: { ...editingService.promo!, endDate: e.target.value }
                              })}
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-2 px-3 text-xs font-bold text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                            />
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              </div>

              <div className="p-6 bg-zinc-50 border-t border-zinc-100 flex flex-col gap-3 shrink-0">
                <button 
                  onClick={handleSave}
                  className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-black text-sm shadow-xl shadow-indigo-600/20 hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 uppercase tracking-widest"
                >
                  <CheckCircle2 className="h-5 w-5" />
                  Salvar Serviço
                </button>
                {editingService.id && (
                  <button 
                    onClick={() => {
                      if (confirm('Deseja excluir este serviço permanentemente?')) {
                        onDeleteService(editingService.id!);
                        setIsModalOpen(false);
                      }
                    }}
                    className="w-full bg-white border border-rose-100 text-rose-500 py-3.5 rounded-2xl font-bold text-sm hover:bg-rose-50 transition-all"
                  >
                    Excluir
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
